import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Panel,
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {useApi} from 'src/hooks/useApi';
import User from 'src/components/crm/lib/user/user';
import {displayDateTime} from 'src/utils/date-time-2';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  SubTitleExtraSmall,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import moment from 'moment';
import css from './view-history-panel.css';


type HistoryItemType =
  | 'APPROVED'
  | 'DISCARDED'
  | 'CANDIDATE_SUBMITTED'
  | 'RECRUITER_UPDATED';

type HistoryItem = {
  id: number;
  updatedBy: number;
  updatedOn: string;
  type: HistoryItemType;
  comment: string;
};

const getTypeConfig = (
  type: HistoryItemType,
  t: (arg1: string, arg2: string) => string,
) => {
  const TYPE_CONFIG = {
    APPROVED: {
      color: 'success',
      icon: 'check-circle',
      label: t('Approved_by', 'Approved by'),
    },
    DISCARDED: {
      color: 'danger',
      icon: 'times-circle',
      label: t('Response_Discarded_by', 'Response Discarded by'),
    },
    CANDIDATE_SUBMITTED: {
      color: 'information',
      icon: 'info-circle',
      label: t('Submitted_by_candidate_on', 'Submitted by candidate on'),
    },
    RECRUITER_UPDATED: {
      color: 'success',
      icon: 'check-circle',
      label: t('Updated_by', 'Updated by'),
    },
  } as const;

  const DEFAULT_TYPE_CONFIG = {
    color: 'neutral',
    icon: 'info-circle',
    label: t('Action_performed_by', 'Action performed by'),
  } as const;
  return TYPE_CONFIG[type] || DEFAULT_TYPE_CONFIG;
};

const HistoryCard = ({item}: {item: HistoryItem}) => {
  const {t, language} = useLabelTranslation();
  const config = getTypeConfig(item.type, t);

  return (
    <Card classNames={{wrapper: css.historyItem}}>
      <Icon color={config.color} name={config.icon} type="solid" />
      <div className={css.historyItemBody}>
        <div className={css.gap}>
          <BodyMediumBold>{config.label}</BodyMediumBold>
          <User userId={item.updatedBy} />
          <SubTitleExtraSmall>
            {moment(item.updatedOn).locale(language).format('DD MMMM, YYYY')}
          </SubTitleExtraSmall>
        </div>
        <div className={css.gap}>
          {item.comment && (
            <>
              <BodyMediumBold>{t('Comment', 'Comment')}: </BodyMediumBold>
              <SubTitleExtraSmall>{item.comment}</SubTitleExtraSmall>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const ViewHistoryPanel = ({
  isOpen,
  onClose,
  formId,
}: {
  isOpen: boolean;
  onClose: () => void;
  formId: number;
}) => {
  const {t} = useLabelTranslation();
  const {result: data, isLoading} = useApi(
    `api/interview-candidate-form/${formId}/history`,
    {
      apiPath: '/trmapi/',
      deps: [],
    },
  );

  return (
    <Panel isOpen={isOpen} onClose={onClose} anchor="right">
      <PanelHeader onCloseButtonClick={onClose}>
        {t('View_History', 'View History')}
      </PanelHeader>
      <PanelBody className={css.panelBody}>
        {isLoading ? (
          <div className={css.loader}>
            <CircularLoader />
          </div>
        ) : (
          // @ts-expect-error - TS2339 - Property 'map' does not exist on type '{}'. | TS7006 - Parameter 'item' implicitly has an 'any' type.
          data && data.map((item) => <HistoryCard key={item.id} item={item} />)
        )}
      </PanelBody>
    </Panel>
  );
};

export default ViewHistoryPanel;
